import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useResponsiveMap } from '@/hooks/useResponsiveMap';

interface MapSizeOptimizationProps {
  isVisible: boolean;
  onClose: () => void;
}

export const MapSizeOptimization: React.FC<MapSizeOptimizationProps> = ({ 
  isVisible, 
  onClose 
}) => {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080
  });

  const { containerHeight, layoutSize, zoom } = useResponsiveMap();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 计算优化前后的对比数据
  const getOptimizationComparison = () => {
    const screenArea = dimensions.width * dimensions.height;
    
    // 优化前：容器高度约为屏幕高度的75%，地图占容器的85%
    const oldContainerHeight = dimensions.height * 0.75;
    const oldMapArea = dimensions.width * oldContainerHeight * 0.85 * 0.85; // 85% × 85%
    
    // 优化后：容器高度为屏幕高度的90%，地图占容器的94%+
    const newContainerHeight = containerHeight;
    const layoutSizePercent = parseFloat(layoutSize.replace('%', '')) / 100;
    const newMapArea = dimensions.width * newContainerHeight * layoutSizePercent * layoutSizePercent;
    
    const improvement = ((newMapArea - oldMapArea) / oldMapArea) * 100;
    
    return {
      screenArea,
      oldMapArea,
      newMapArea,
      improvement,
      oldContainerHeight,
      newContainerHeight,
      oldMapPercent: (oldMapArea / screenArea) * 100,
      newMapPercent: (newMapArea / screenArea) * 100
    };
  };

  const comparison = getOptimizationComparison();

  if (!isVisible) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题 */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">📊 地图尺寸优化效果</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ✕
          </button>
        </div>

        {/* 优化策略说明 */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">🎯 优化策略</h3>
          <div className="text-blue-700 space-y-1">
            <p>• <strong>容器高度</strong>：从屏幕高度的75% → 90%</p>
            <p>• <strong>地图布局</strong>：从85%占比 → {layoutSize}占比</p>
            <p>• <strong>缩放系数</strong>：从15倍系数 → 12倍系数（更大缩放）</p>
            <p>• <strong>最大缩放</strong>：从3.5倍 → 5.0倍限制</p>
          </div>
        </div>

        {/* 数据对比 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* 优化前 */}
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <h3 className="text-lg font-semibold text-red-800 mb-3">📉 优化前</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-red-700">屏幕尺寸:</span>
                <span className="font-mono">{dimensions.width}×{dimensions.height}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-700">容器高度:</span>
                <span className="font-mono">{Math.round(comparison.oldContainerHeight)}px (75%)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-700">地图占比:</span>
                <span className="font-mono">85% × 85%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-700">地图面积:</span>
                <span className="font-mono">{Math.round(comparison.oldMapArea).toLocaleString()}px²</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-700">屏幕利用率:</span>
                <span className="font-mono font-bold">{comparison.oldMapPercent.toFixed(1)}%</span>
              </div>
            </div>
          </div>

          {/* 优化后 */}
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <h3 className="text-lg font-semibold text-green-800 mb-3">📈 优化后</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-green-700">屏幕尺寸:</span>
                <span className="font-mono">{dimensions.width}×{dimensions.height}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">容器高度:</span>
                <span className="font-mono">{Math.round(comparison.newContainerHeight)}px (90%)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">地图占比:</span>
                <span className="font-mono">{layoutSize} × {layoutSize}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">地图面积:</span>
                <span className="font-mono">{Math.round(comparison.newMapArea).toLocaleString()}px²</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">屏幕利用率:</span>
                <span className="font-mono font-bold">{comparison.newMapPercent.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* 改进效果 */}
        <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <h3 className="text-lg font-semibold text-yellow-800 mb-3">🚀 改进效果</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                +{comparison.improvement.toFixed(1)}%
              </div>
              <div className="text-sm text-yellow-700">地图面积增加</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                +{(comparison.newContainerHeight - comparison.oldContainerHeight).toFixed(0)}px
              </div>
              <div className="text-sm text-yellow-700">容器高度增加</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {zoom.toFixed(1)}x
              </div>
              <div className="text-sm text-yellow-700">当前缩放级别</div>
            </div>
          </div>
        </div>

        {/* 可视化对比 */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">📐 可视化对比</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 优化前示意图 */}
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-2">优化前</div>
              <div className="relative bg-gray-200 rounded" style={{ aspectRatio: '16/9' }}>
                <div 
                  className="absolute bg-red-300 rounded"
                  style={{
                    top: '12.5%',
                    left: '7.5%',
                    width: '85%',
                    height: '75%'
                  }}
                >
                  <div 
                    className="absolute bg-red-500 rounded"
                    style={{
                      top: '7.5%',
                      left: '7.5%',
                      width: '85%',
                      height: '85%'
                    }}
                  />
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1">地图占屏幕 {comparison.oldMapPercent.toFixed(1)}%</div>
            </div>

            {/* 优化后示意图 */}
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-2">优化后</div>
              <div className="relative bg-gray-200 rounded" style={{ aspectRatio: '16/9' }}>
                <div 
                  className="absolute bg-green-300 rounded"
                  style={{
                    top: '5%',
                    left: '3%',
                    width: '94%',
                    height: '90%'
                  }}
                >
                  <div 
                    className="absolute bg-green-500 rounded"
                    style={{
                      top: `${(100 - parseFloat(layoutSize.replace('%', ''))) / 2}%`,
                      left: `${(100 - parseFloat(layoutSize.replace('%', ''))) / 2}%`,
                      width: layoutSize,
                      height: layoutSize
                    }}
                  />
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1">地图占屏幕 {comparison.newMapPercent.toFixed(1)}%</div>
            </div>
          </div>
        </div>

        {/* 实际效果说明 */}
        <div className="p-4 bg-green-50 rounded-lg">
          <h4 className="font-semibold text-green-800 mb-2">✅ 实际效果</h4>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• 地图显示面积增加 <strong>{comparison.improvement.toFixed(1)}%</strong></li>
            <li>• 省份细节更加清晰，边界更容易识别</li>
            <li>• 拼图操作精确度大幅提升</li>
            <li>• 在大屏幕上获得接近全屏的地图体验</li>
            <li>• 仍然保持完整显示，不会被裁切</li>
          </ul>
        </div>

        {/* 关闭按钮 */}
        <div className="mt-6 text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            关闭优化分析
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};
